import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import Services from './components/Services';
import Projects from './pages/Projects';
import Analytics from './pages/Analytics';
import Team from './pages/Team';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Finance from './pages/Finance';
import GoogleAds from './pages/GoogleAds';
import FacebookAds from './pages/FacebookAds';
import GBP from './pages/GBP';
import SEOAudit from './pages/SEOAudit';
import GoogleCallback from './pages/GoogleCallback';
import Hiring from './pages/Hiring';
import Requests from './pages/Requests';
import Outreach from './pages/Outreach';
import LandingPages from './pages/LandingPages';
import Tracking from './pages/Tracking';
import SalesPipeline from './pages/SalesPipeline';
import Prospects from './pages/Prospects';
import ClientOnboarding from './pages/ClientOnboarding';
import Email from './pages/Email';
import Calls from './pages/Calls';
import LeadGenDashboard from './pages/LeadGenDashboard';
import LeadGenLeads from './pages/LeadGenLeads';
import PhoneSettings from './pages/PhoneSettings';
import ContactSubmissions from './pages/ContactSubmissions';
import EmailCampaigns from './pages/EmailCampaigns';
import LocalRankTracker from './pages/LocalRankTracker';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ClientProvider } from './contexts/ClientContext';
import { InternalRoute, ClientRoute, LeadGenRoute } from './components/ModeRoute';
import './App.css';

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <ClientProvider>{children}</ClientProvider>;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard - available in both modes */}
        <Route index element={<Dashboard />} />

        {/* Internal Mode routes - Business operations */}
        <Route path="projects" element={<InternalRoute><Projects /></InternalRoute>} />
        <Route path="clients" element={<InternalRoute><Clients /></InternalRoute>} />
        <Route path="clients/:clientId" element={<InternalRoute><ClientDetail /></InternalRoute>} />
        <Route path="clients/:clientId/onboarding" element={<InternalRoute><ClientOnboarding /></InternalRoute>} />
        <Route path="finance" element={<InternalRoute><Finance /></InternalRoute>} />
        <Route path="team" element={<InternalRoute><Team /></InternalRoute>} />
        <Route path="hiring" element={<InternalRoute><Hiring /></InternalRoute>} />
        <Route path="analytics" element={<InternalRoute><Analytics /></InternalRoute>} />
        <Route path="requests" element={<InternalRoute><Requests /></InternalRoute>} />
        <Route path="tracking" element={<InternalRoute><Tracking /></InternalRoute>} />
        <Route path="pipeline" element={<InternalRoute><SalesPipeline /></InternalRoute>} />
        <Route path="prospects" element={<InternalRoute><Prospects /></InternalRoute>} />
        <Route path="email" element={<InternalRoute><Email /></InternalRoute>} />
        <Route path="services" element={<InternalRoute><Services /></InternalRoute>} />
        <Route path="contact-submissions" element={<InternalRoute><ContactSubmissions /></InternalRoute>} />
        <Route path="email-campaigns" element={<InternalRoute><EmailCampaigns /></InternalRoute>} />

        {/* Client Mode routes - Marketing services */}
        <Route path="google-ads" element={<ClientRoute><GoogleAds /></ClientRoute>} />
        <Route path="facebook-ads" element={<ClientRoute><FacebookAds /></ClientRoute>} />
        <Route path="gbp" element={<ClientRoute><GBP /></ClientRoute>} />
        <Route path="seo" element={<ClientRoute><SEOAudit /></ClientRoute>} />
        <Route path="local-rankings" element={<ClientRoute><LocalRankTracker /></ClientRoute>} />
        <Route path="outreach" element={<ClientRoute><Outreach /></ClientRoute>} />
        <Route path="landing-pages" element={<ClientRoute><LandingPages /></ClientRoute>} />
        <Route path="calls" element={<ClientRoute><Calls /></ClientRoute>} />
        <Route path="phone-settings" element={<ClientRoute><PhoneSettings /></ClientRoute>} />

        {/* Lead Generation routes */}
        <Route path="leadgen" element={<LeadGenRoute><LeadGenDashboard /></LeadGenRoute>} />
        <Route path="leadgen/leads" element={<LeadGenRoute><LeadGenLeads /></LeadGenRoute>} />

        {/* OAuth callbacks - available in both modes */}
        <Route path="auth/google/callback" element={<GoogleCallback />} />
        <Route path="auth/callback" element={<GoogleCallback />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <AppRoutes />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
